import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { rolesApi, Role, CreateRoleRequest, UpdateRoleRequest } from '../services/rolesApi'

interface RolesState {
  roles: Role[]
  loading: boolean
  error: string | null
}

interface RolesActions {
  fetchRoles: () => Promise<void>
  createRole: (roleData: CreateRoleRequest) => Promise<void>
  updateRole: (roleId: string, roleData: UpdateRoleRequest) => Promise<void>
  deleteRole: (roleId: string) => Promise<void>
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
}

type RolesStore = RolesState & RolesActions

export const useRolesStore = create<RolesStore>()(
  persist(
    (set, get) => ({
      // Initial state
      roles: [],
      loading: false,
      error: null,

      // Actions
      fetchRoles: async () => {
        set({ loading: true, error: null })
        
        try {
          const response = await rolesApi.getRoles()
          
          if (response.success && response.data) {
            set({
              roles: response.data,
              loading: false,
              error: null,
            })
          } else {
            set({
              roles: [],
              loading: false,
              error: 'Failed to fetch roles',
            })
          }
        } catch (error: any) {
          set({
            roles: [],
            loading: false,
            error: error.message || 'Failed to fetch roles',
          })
        }
      },

      createRole: async (roleData: CreateRoleRequest) => {
        set({ loading: true, error: null })
        
        try {
          const response = await rolesApi.createRole(roleData)
          
          if (response.success && response.data) {
            const { roles } = get()
            set({
              roles: [...roles, response.data],
              loading: false,
              error: null,
            })
          } else {
            set({
              loading: false,
              error: 'Failed to create role',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to create role',
          })
        }
      },

      updateRole: async (roleId: string, roleData: UpdateRoleRequest) => {
        set({ loading: true, error: null })
        
        try {
          const response = await rolesApi.updateRole(roleId, roleData)
          
          if (response.success && response.data) {
            const { roles } = get()
            set({
              roles: roles.map(role => 
                role._id === roleId ? response.data! : role
              ),
              loading: false,
              error: null,
            })
          } else {
            set({
              loading: false,
              error: 'Failed to update role',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to update role',
          })
        }
      },

      deleteRole: async (roleId: string) => {
        set({ loading: true, error: null })
        
        try {
          const response = await rolesApi.deleteRole(roleId)
          
          if (response.success) {
            const { roles } = get()
            set({
              roles: roles.filter(role => role._id !== roleId),
              loading: false,
              error: null,
            })
          } else {
            set({
              loading: false,
              error: 'Failed to delete role',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to delete role',
          })
        }
      },

      setLoading: (loading: boolean) => {
        set({ loading })
      },

      setError: (error: string | null) => {
        set({ error })
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'roles-storage',
      partialize: (state) => ({
        roles: state.roles,
      }),
    }
  )
)
